import { useCallback, useEffect, useMemo } from 'react';
import { RoiType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { Chart } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { featureFlags } from '../../../feature-flags';
import { trackingIds } from '../../../tracking-ids';
import { useFetchMessagingROIOOverview } from '../hooks/use-fetch-messaging-roi-overview';
import { useFetchRoiTrendLine } from '../hooks/use-fetch-roi-trend-line';
import { useHasRoiFeature } from '../hooks/use-has-roi-feature';
import { emptyStateConfigForGauge, gaugeAppearance } from './constants';
import { renderCustomCenterLabel } from './custom-gauge-label';
import { RoiOverviewCard } from './roi-overview-card';
import { SingleRoiCard } from './single-roi-card';
import { OverviewCardProps, RoiCardBaseProps } from './types';

export const MCTRoiOverviewCard = ({
  handleUpdateActiveCards,
  hasOnlyOneCard,
  timePeriod,
  ...rest
}: OverviewCardProps) => {
  const { t } = useTranslation('analytics');
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.missedCallTextRoiDemoData);
  const { isFeatureEnabled: isMCTRoiEligibleLocation } = useHasRoiFeature({ roi: 'missedCallText' });
  const navigate = useNavigate();

  const payload = {
    additionalRequestPayload: {
      roiType: RoiType.MISSED_CALL_TEXT,
    },
    filters: {
      startDate: timePeriod.value.startDate,
      endDate: timePeriod.value.endDate,
      locationIds: selectedLocationIds,
    },
    isDemoAccount,
    isEnabled: isMCTRoiEligibleLocation,
  };

  const { aggregated, isLoading } = useFetchMessagingROIOOverview(payload);
  const { isLoading: isLoadingTrendLine, trendLine } = useFetchRoiTrendLine({
    ...payload,
    isEnabled: payload.isEnabled && hasOnlyOneCard,
  });

  const messagesSent = aggregated?.leadsContacted?.smsCount || 0;
  const appointmentsScheduled = aggregated?.patientsScheduled?.appointmentsCount || 0;

  const redirectHandler = useCallback(() => {
    navigate({
      to: '/analytics/roi/missed-call-text-conversion',
    });
  }, []);

  const commonProps: RoiCardBaseProps = useMemo(
    () => ({
      ...rest,
      filteredDate: timePeriod.label,
      onClick: redirectHandler,
      summaryValues: [
        {
          className: 'messages-sent',
          label: t('Messages Sent'),
          value: formatters.value(messagesSent),
        },
        {
          className: 'appointments-scheduled',
          label: t('Appointments Scheduled'),
          value: formatters.value(appointmentsScheduled),
        },
      ],
      title: t('Missed Call Text Conversion'),
      trackingId: trackingIds.analytics.overviewMissedCallTextRoi,
    }),
    [appointmentsScheduled, messagesSent, rest]
  );

  useEffect(() => {
    handleUpdateActiveCards('missedCallText', !!appointmentsScheduled);
  }, [appointmentsScheduled]);

  if (!appointmentsScheduled) {
    return null;
  }

  if (hasOnlyOneCard) {
    return (
      <SingleRoiCard
        {...commonProps}
        isDemoAccount={isDemoAccount}
        isLoading={isLoading || isLoadingTrendLine}
        summaryValues={[
          ...commonProps.summaryValues,
          {
            className: 'appointments-completed',
            label: t('Completed Appointments'),
            value: formatters.value(aggregated?.appointmentsCompleted?.appointmentsCount ?? 0),
          },
        ]}
        trendLine={trendLine}
      />
    );
  }

  return (
    <RoiOverviewCard {...commonProps} iconName='message' isLoading={isLoading}>
      <Chart
        colors={{ value: theme.colors.primary20 }}
        emptyStateConfig={emptyStateConfigForGauge}
        noBorder
        style={{ marginBottom: 0 }}
      >
        <Chart.GaugeChart
          appearance={gaugeAppearance}
          customCenterLabel={() => renderCustomCenterLabel(t('{{count}} Messages', { count: messagesSent }))}
          value={(appointmentsScheduled / (messagesSent || 1)) * 100}
        />
      </Chart>
    </RoiOverviewCard>
  );
};
